import * as classNames from "classNames";
import * as React from "react";
import { CommonHelper } from "../../../../../react-core/Helpers/CommonHelper";
import { Lightbox, LightboxType } from "../../../../../react-core/Html/Components/Lightbox";
import { FormComponent } from "../../../../../react-core/Html/FormComponent";
import { NumberTextBoxEditor } from "../../../../../react-core/Html/NumberTextBoxEditor";
import { PartLoading } from "../../../../../react-core/Html/PartLoading";
import { PercentageTextBoxEditor } from "../../../../../react-core/Html/PercentageTextBoxEditor";
import { RadiosEditor } from "../../../../../react-core/Html/RadiosEditor";
import { TextBoxEditor } from "../../../../../react-core/Html/TextBoxEditor";
import { PensionProtectionDetails } from "../../../../../Wrap/Models/Models/Customer/PensionProtection/PensionProtectionDetails";
import { ClientRecordAction } from "./Action/ClientRecordAction";
import {
    ClientRecordTab,
    GrantedTypes,
    GrantedTypesMap,
    IClientTabProps,
    PensionProtectionMap,
    PensionProtectionMapForDrawdown,
} from "./Resource/Contents";
import { ClientRecordStore } from "./Store/Variable";
import { CustomerPensionProtectionModel } from "../../../../../Wrap/Models/Domains/Drawdown/CustomerPensionProtectionModel";
import { LifetimeAllowanceEnhancementType } from "../../../../../Wrap/Constants/Enums/Domain/LifetimeAllowanceEnhancementType";

interface IPensionProtectionState {
    PensionProtection: PensionProtectionDetails;
    _data: PensionProtectionDetails;
    customerPensionProtectionModel: CustomerPensionProtectionModel;
    isLoading: boolean;
    errorMsg: string[];
    isTYE2024Change: boolean;
    allowEditBlockTransfer: boolean;
}

interface IPensionProtectionProps extends IClientTabProps {
    callback?: () => void;
    hideHeading?: boolean;
    hideContainerClasses?: boolean;
    onSaveBack?: (result) => void;
    subaccountInstructionHierarchyId?: string;
    hasResetPensionProtection?: boolean;
    disabled?: boolean;
    isNewInstruction?: boolean;
    pensionProtection?: PensionProtectionDetails;
    displaySubmittedOptionsOnly?: boolean;
}

export class PensionProtection extends FormComponent<IPensionProtectionProps, IPensionProtectionState> {
    static defaultProps = {
        hasResetPensionProtection: false,
    };

    constructor(props: IPensionProtectionProps) {
        super(props);
        this.state = {
            PensionProtection: null,
            _data: null,
            customerPensionProtectionModel: null,
            isLoading: true,
            errorMsg: [],
            isTYE2024Change: true,
            allowEditBlockTransfer: false
        };
        this.subscribeToStore(ClientRecordStore, () => this.onClientRecordStoreChange());
    }

    private onClientRecordStoreChange() {
        const data = ClientRecordStore.getData();
        if (!this.props.hasResetPensionProtection) {
            const model = this.populatePensionProtection(data[ClientRecordTab.PensionProtection]);
            this.setState({
                isLoading: false,
                customerPensionProtectionModel: data[ClientRecordTab.PensionProtection],
                PensionProtection: model,
                _data: JSON.parse(JSON.stringify(model)),
            });
        } else {
            this.setState({
                isLoading: false,
                PensionProtection: this.emptyPensionProtection(),
                _data: this.emptyPensionProtection(),
                allowEditBlockTransfer: true
            });
        }
    }

    private populatePensionProtection(model: CustomerPensionProtectionModel): PensionProtectionDetails {
        let pensionEnhancedProtectionDetails = null;
        if (model?.Enhanced?.MaximumTFEFromArrAt05042023 ||
            model?.Enhanced?.MaximumTFEFromArrAt05042024 ||
            model?.Enhanced?.TotalAccumulationValueAt05042024
        ) {
            pensionEnhancedProtectionDetails = {
                CertificateNumber: model.Enhanced != null ? model.Enhanced.CertificateNumber : null,
                PclsEntitlement: model.Enhanced != null ? model.Enhanced.EntitlementPercentage : null,
                HasBeenAppliedBefore15032023:
                    model.Enhanced != null ? model.Enhanced.HasBeenAppliedBefore15032023 : null,
                MaximumTFEFromArrAt05042023: model.Enhanced != null ? model.Enhanced.MaximumTFEFromArrAt05042023 : null,
                TotalAccumulationValueAt05042024: model.Enhanced != null ? model.Enhanced.TotalAccumulationValueAt05042024 : null,
                MaximumTFEFromArrAt05042024: model.Enhanced.MaximumTFEFromArrAt05042024,
            };
        }

        let pensionPrimaryProtectionDetails = null;
        if (
            model?.Primary?.CertificateNumber != null ||
            model?.Primary?.EnhancementFactor != null ||
            model?.Primary?.ProtectionAmount
        ) {
            pensionPrimaryProtectionDetails = {
                CertificateNumber: model.Primary != null ? model.Primary.CertificateNumber : null,
                EnhancementFactor: model.Primary != null ? model.Primary.EnhancementFactor : null,
                PclsEntitlement: model.Primary != null ? model.Primary.ProtectionAmount : null,
            };
        }

        const fixed2012 = model?.Fixed?.filter((x) => x.Year == 2012 && x?.HasProtection);
        const pensionFixed2012ProtectionDetails =
            fixed2012 && fixed2012.length > 0
                ? {
                    CertificateNumber: fixed2012[0].CertificateNumber,
                    HasBeenAppliedBefore15032023: fixed2012[0]?.HasBeenAppliedBefore15032023 ?? false,
                }
                : null;

        const fixed2014 = model?.Fixed?.filter((x) => x.Year == 2014 && x?.HasProtection);
        const pensionFixed2014ProtectionDetails =
            fixed2014 && fixed2014.length > 0
                ? {
                    CertificateNumber: fixed2014[0].CertificateNumber,
                    HasBeenAppliedBefore15032023: fixed2014[0]?.HasBeenAppliedBefore15032023 ?? false,
                }
                : null;

        const fixed2016 = model?.Fixed?.filter((x) => x.Year == 2016 && x?.HasProtection);
        const pensionFixed2016ProtectionDetails =
            fixed2016 && fixed2016.length > 0
                ? {
                    CertificateNumber: fixed2016[0].CertificateNumber,
                    HasBeenAppliedBefore15032023: fixed2016[0]?.HasBeenAppliedBefore15032023 ?? false,
                }
                : null;

        const individual2014 = model?.Individual?.filter((x) => x.Year == 2014 && x?.HasProtection);
        const pensionIndividual2014ProtectionDetails =
            individual2014 && individual2014.length > 0
                ? {
                    CertificateNumber: individual2014[0].CertificateNumber,
                    Amount: individual2014[0].Entitlement,
                }
                : null;

        const individual2016 = model?.Individual?.filter((x) => x.Year == 2016 && x?.HasProtection);
        const pensionIndividual2016ProtectionDetails =
            individual2016 && individual2016.length > 0
                ? {
                    CertificateNumber: individual2016[0].CertificateNumber,
                    Amount: individual2016[0].Entitlement,
                }
                : null;

        let pensionSchemeSpecificProtectionDetails = null;
        if (model?.PensionSchemeSpecificProtection?.HasProtection) {
            pensionSchemeSpecificProtectionDetails = {
                IsBlockTransferProtected: model?.PensionSchemeSpecificProtection?.IsBlockTransferProtected,
                ProtectedRetirementAge: model?.PensionSchemeSpecificProtection?.ProtectedRetirementAge,
                ADayFundValue: model?.PensionSchemeSpecificProtection?.ADayFundValue,
                PclsEntitlement: model?.PensionSchemeSpecificProtection?.PCLSEntitlement,
            };
            if (!model?.PensionSchemeSpecificProtection?.IsWraphubBlockTransferProtected) {
                this.setState({
                    allowEditBlockTransfer: true
                })
            }
        }
        else {
            this.setState({
                allowEditBlockTransfer: true
            })
        }

        let pensionTransitionalLumpSumProtectionDetails = null;
        if (model?.TransitionalLumpSum?.HasProtection) {
            pensionTransitionalLumpSumProtectionDetails = {
                TransitionalLumpSumAllowance: model?.TransitionalLumpSum?.TransitionalLumpSumAllowance,
                TransitionalLumpSumDeathBenefitAllowance: model?.TransitionalLumpSum?.TransitionalLumpSumDeathBenefitAllowance,
            };
        }

        let pensionRelevantOverseasProtectionDetails = null;
        let overseasIndividuals = model?.LifetimeAllowanceEnhancements?.filter(
            (x) => x.Type === LifetimeAllowanceEnhancementType.OverseasIndividual
        );
        if (overseasIndividuals?.length > 0) {
            pensionRelevantOverseasProtectionDetails = {
                CertificateNumber: overseasIndividuals[0]?.CertificateNumber,
                EnhancementFactor: overseasIndividuals[0]?.EnhancementFactor,
                IsPre6Apr2012: overseasIndividuals[0]?.GrantedPrior2012,
                IsPre6Apr2014: overseasIndividuals[0]?.GrantedPrior2014,
                IsPre6Apr2016: overseasIndividuals[0]?.GrantedPrior2016,
                IsPost6Apr2016: overseasIndividuals[0]?.GrantedPost2016,
            };
        }

        let pensionQropsProtectionDetails = null;
        let overseasPensionSchemes = model?.LifetimeAllowanceEnhancements?.filter(
            (x) => x.Type === LifetimeAllowanceEnhancementType.OverseasPensionScheme
        );
        if (overseasPensionSchemes?.length > 0) {
            pensionQropsProtectionDetails = {
                CertificateNumber: overseasPensionSchemes[0]?.CertificateNumber,
                EnhancementFactor: overseasPensionSchemes[0]?.EnhancementFactor,
                IsPre6Apr2012: overseasPensionSchemes[0]?.GrantedPrior2012,
                IsPre6Apr2014: overseasPensionSchemes[0]?.GrantedPrior2014,
                IsPre6Apr2016: overseasPensionSchemes[0]?.GrantedPrior2016,
                IsPost6Apr2016: overseasPensionSchemes[0]?.GrantedPost2016,
            };
        }

        let pensionCreditProtectionDetails = null;
        let receiptOfPensionCredits = model?.LifetimeAllowanceEnhancements?.filter(
            (x) => x.Type === LifetimeAllowanceEnhancementType.ReceiptOfPensionCredit
        );
        if (receiptOfPensionCredits?.length > 0) {
            pensionCreditProtectionDetails = {
                CertificateNumber: receiptOfPensionCredits[0]?.CertificateNumber,
                EnhancementFactor: receiptOfPensionCredits[0]?.EnhancementFactor,
                IsPre6Apr2012: receiptOfPensionCredits[0]?.GrantedPrior2012,
                IsPre6Apr2014: receiptOfPensionCredits[0]?.GrantedPrior2014,
                IsPre6Apr2016: receiptOfPensionCredits[0]?.GrantedPrior2016,
                IsPost6Apr2016: receiptOfPensionCredits[0]?.GrantedPost2016,
            };
        }

        return {
            PensionCreditProtectionDetails: pensionCreditProtectionDetails,
            PensionEnhancedProtectionDetails: pensionEnhancedProtectionDetails,
            PensionPrimaryProtectionDetails: pensionPrimaryProtectionDetails,
            PensionFixed2014ProtectionDetails: pensionFixed2014ProtectionDetails,
            PensionFixed2016ProtectionDetails: pensionFixed2016ProtectionDetails,
            PensionFixedProtectionDetails: pensionFixed2012ProtectionDetails,
            PensionIndividual2014ProtectionDetails: pensionIndividual2014ProtectionDetails,
            PensionIndividual2016ProtectionDetails: pensionIndividual2016ProtectionDetails,
            PensionRelevantOverseasProtectionDetails: pensionRelevantOverseasProtectionDetails,
            PensionSchemeSpecificProtectionDetails: pensionSchemeSpecificProtectionDetails,
            PensionSevereIllHealthDeclarationDetails: null,
            PensionQropsProtectionDetails: pensionQropsProtectionDetails,
            PensionTransitionalLumpSumProtectionDetails: pensionTransitionalLumpSumProtectionDetails
        };
    }

    private mapPensionProtectionToCustomerPensionProtection(
        pensionProtectionDetails: PensionProtectionDetails
    ): CustomerPensionProtectionModel {
        const customerPensionEnhancedProtection = {
            HasProtection:
                pensionProtectionDetails.PensionEnhancedProtectionDetails != null &&
                (pensionProtectionDetails.PensionEnhancedProtectionDetails?.MaximumTFEFromAllAt05042023 != null ||
                    pensionProtectionDetails.PensionEnhancedProtectionDetails?.MaximumTFEFromArrAt05042023 != null ||
                    pensionProtectionDetails.PensionEnhancedProtectionDetails?.MaximumTFEFromArrAt05042024 != null
                ),
            CertificateNumber: pensionProtectionDetails.PensionEnhancedProtectionDetails?.CertificateNumber,
            EntitlementPercentage: pensionProtectionDetails.PensionEnhancedProtectionDetails?.PclsEntitlement,
            HasBeenAppliedBefore15032023:
                pensionProtectionDetails.PensionEnhancedProtectionDetails?.HasBeenAppliedBefore15032023,
            MaximumTFEFromAllAt05042023: pensionProtectionDetails.PensionEnhancedProtectionDetails?.MaximumTFEFromAllAt05042023,
            MaximumTFEFromArrAt05042023: pensionProtectionDetails.PensionEnhancedProtectionDetails?.MaximumTFEFromArrAt05042023,
            TotalAccumulationValueAt05042024: pensionProtectionDetails.PensionEnhancedProtectionDetails?.TotalAccumulationValueAt05042024,
            MaximumTFEFromArrAt05042024: pensionProtectionDetails.PensionEnhancedProtectionDetails?.MaximumTFEFromArrAt05042024,

        };

        let fixed = [];
        if (pensionProtectionDetails.PensionFixedProtectionDetails != null) {
            fixed.push({
                HasProtection: true,
                Year: 2012,
                CertificateNumber: pensionProtectionDetails.PensionFixedProtectionDetails.CertificateNumber,
                HasBeenAppliedBefore15032023:
                    pensionProtectionDetails.PensionFixedProtectionDetails.HasBeenAppliedBefore15032023,
            });
        } else {
            fixed.push({
                HasProtection: false,
                Year: 2012,
                CertificateNumber: null,
                HasBeenAppliedBefore15032023: false,
            });
        }

        if (pensionProtectionDetails.PensionFixed2014ProtectionDetails != null) {
            fixed.push({
                HasProtection: true,
                Year: 2014,
                CertificateNumber: pensionProtectionDetails.PensionFixed2014ProtectionDetails.CertificateNumber,
                HasBeenAppliedBefore15032023:
                    pensionProtectionDetails.PensionFixed2014ProtectionDetails.HasBeenAppliedBefore15032023,
            });
        } else {
            fixed.push({
                HasProtection: false,
                Year: 2014,
                CertificateNumber: null,
                HasBeenAppliedBefore15032023: false,
            });
        }

        if (pensionProtectionDetails.PensionFixed2016ProtectionDetails != null) {
            fixed.push({
                HasProtection: true,
                Year: 2016,
                CertificateNumber: pensionProtectionDetails.PensionFixed2016ProtectionDetails.CertificateNumber,
                HasBeenAppliedBefore15032023:
                    pensionProtectionDetails.PensionFixed2016ProtectionDetails.HasBeenAppliedBefore15032023,
            });
        } else {
            fixed.push({
                HasProtection: false,
                Year: 2016,
                CertificateNumber: null,
                HasBeenAppliedBefore15032023: false,
            });
        }

        let individual = [];
        if (pensionProtectionDetails.PensionIndividual2014ProtectionDetails != null) {
            individual.push({
                HasProtection: true,
                Year: 2014,
                Entitlement: pensionProtectionDetails.PensionIndividual2014ProtectionDetails?.Amount,
                CertificateNumber: pensionProtectionDetails.PensionIndividual2014ProtectionDetails?.CertificateNumber,
            });
        } else {
            individual.push({
                HasProtection: false,
                Year: 2014,
                Entitlement: 0,
                CertificateNumber: null,
            });
        }

        if (pensionProtectionDetails.PensionIndividual2016ProtectionDetails != null) {
            individual.push({
                HasProtection: true,
                Year: 2016,
                Entitlement: pensionProtectionDetails.PensionIndividual2016ProtectionDetails?.Amount,
                CertificateNumber: pensionProtectionDetails.PensionIndividual2016ProtectionDetails?.CertificateNumber,
            });
        } else {
            individual.push({
                HasProtection: false,
                Year: 2016,
                Entitlement: 0,
                CertificateNumber: null,
            });
        }

        let lifetimeAllowanceEnhancements = [];
        if (pensionProtectionDetails?.PensionRelevantOverseasProtectionDetails != null) {
            lifetimeAllowanceEnhancements.push({
                Type: LifetimeAllowanceEnhancementType.OverseasIndividual,
                GrantedPrior2012: pensionProtectionDetails?.PensionRelevantOverseasProtectionDetails?.IsPre6Apr2012,
                GrantedPrior2014: pensionProtectionDetails?.PensionRelevantOverseasProtectionDetails?.IsPre6Apr2014,
                GrantedPrior2016: pensionProtectionDetails?.PensionRelevantOverseasProtectionDetails?.IsPre6Apr2016,
                GrantedPost2016: pensionProtectionDetails?.PensionRelevantOverseasProtectionDetails?.IsPost6Apr2016,
                CertificateNumber:
                    pensionProtectionDetails?.PensionRelevantOverseasProtectionDetails?.CertificateNumber,
                EnhancementFactor:
                    pensionProtectionDetails?.PensionRelevantOverseasProtectionDetails?.EnhancementFactor,
            });
        }

        if (pensionProtectionDetails?.PensionQropsProtectionDetails != null) {
            lifetimeAllowanceEnhancements.push({
                Type: LifetimeAllowanceEnhancementType.OverseasPensionScheme,
                GrantedPrior2012: pensionProtectionDetails?.PensionQropsProtectionDetails?.IsPre6Apr2012,
                GrantedPrior2014: pensionProtectionDetails?.PensionQropsProtectionDetails?.IsPre6Apr2014,
                GrantedPrior2016: pensionProtectionDetails?.PensionQropsProtectionDetails?.IsPre6Apr2016,
                GrantedPost2016: pensionProtectionDetails?.PensionQropsProtectionDetails?.IsPost6Apr2016,
                CertificateNumber: pensionProtectionDetails?.PensionQropsProtectionDetails?.CertificateNumber,
                EnhancementFactor: pensionProtectionDetails?.PensionQropsProtectionDetails?.EnhancementFactor,
            });
        }

        if (pensionProtectionDetails?.PensionCreditProtectionDetails != null) {
            lifetimeAllowanceEnhancements.push({
                Type: LifetimeAllowanceEnhancementType.ReceiptOfPensionCredit,
                GrantedPrior2012: pensionProtectionDetails?.PensionCreditProtectionDetails?.IsPre6Apr2012,
                GrantedPrior2014: pensionProtectionDetails?.PensionCreditProtectionDetails?.IsPre6Apr2014,
                GrantedPrior2016: pensionProtectionDetails?.PensionCreditProtectionDetails?.IsPre6Apr2016,
                GrantedPost2016: pensionProtectionDetails?.PensionCreditProtectionDetails?.IsPost6Apr2016,
                CertificateNumber: pensionProtectionDetails?.PensionCreditProtectionDetails?.CertificateNumber,
                EnhancementFactor: pensionProtectionDetails?.PensionCreditProtectionDetails?.EnhancementFactor,
            });
        }

        const primary = {
            HasProtection:
                pensionProtectionDetails.PensionPrimaryProtectionDetails != null &&
                (pensionProtectionDetails.PensionPrimaryProtectionDetails?.CertificateNumber != null ||
                    (pensionProtectionDetails.PensionPrimaryProtectionDetails?.EnhancementFactor != null)),
            CertificateNumber: pensionProtectionDetails.PensionPrimaryProtectionDetails?.CertificateNumber,
            EnhancementFactor: pensionProtectionDetails.PensionPrimaryProtectionDetails?.EnhancementFactor,
            ProtectionAmount: pensionProtectionDetails.PensionPrimaryProtectionDetails?.PclsEntitlement,
        };

        const pensionSchemeSpecificProtection = {
            HasProtection: pensionProtectionDetails.PensionSchemeSpecificProtectionDetails != null,
            IsBlockTransferProtected:
                pensionProtectionDetails.PensionSchemeSpecificProtectionDetails?.IsBlockTransferProtected,
            ProtectedRetirementAge:
                pensionProtectionDetails.PensionSchemeSpecificProtectionDetails?.ProtectedRetirementAge,
            ADayFundValue: pensionProtectionDetails.PensionSchemeSpecificProtectionDetails?.ADayFundValue,
            PCLSEntitlement: pensionProtectionDetails.PensionSchemeSpecificProtectionDetails?.PclsEntitlement,
        };

        const pensionSchemeSpecificProtectionDetails = {
            HasProtection: pensionProtectionDetails.PensionTransitionalLumpSumProtectionDetails != null,
            TransitionalLumpSumAllowance: pensionProtectionDetails.PensionTransitionalLumpSumProtectionDetails?.TransitionalLumpSumAllowance,
            TransitionalLumpSumDeathBenefitAllowance: pensionProtectionDetails.PensionTransitionalLumpSumProtectionDetails?.TransitionalLumpSumDeathBenefitAllowance,
        }

        let result: CustomerPensionProtectionModel = {
            Enhanced: customerPensionEnhancedProtection,
            Fixed: fixed,
            Individual: individual,
            LifetimeAllowanceEnhancements: lifetimeAllowanceEnhancements,
            Primary: primary,
            PensionSchemeSpecificProtection: pensionSchemeSpecificProtection,
            TransitionalLumpSum: pensionSchemeSpecificProtectionDetails
        };
        result.HasAnyProtection = this.populateHasAnyProtection(result);
        return result;
    }

    private populateHasAnyProtection(source: CustomerPensionProtectionModel): boolean {
        return source?.Enhanced?.HasProtection ||
            source?.Fixed?.some(x => x.HasProtection) ||
            source?.Individual?.some(x => x.HasProtection) ||
            source?.LifetimeAllowanceEnhancements?.length > 0 ||
            source?.Primary?.HasProtection ||
            source?.PensionSchemeSpecificProtection?.HasProtection ||
            source?.TransitionalLumpSum?.HasProtection;
    }

    private onSave() {
        const { PensionProtection } = this.state;
        if (super.isValid()) {
            this.setState({ isLoading: true });

            if (this.props.isNewInstruction && CommonHelper.isNullOrEmpty(this.props.subaccountInstructionHierarchyId)) {
                const customerPensionProtection = this.mapPensionProtectionToCustomerPensionProtection(
                    PensionProtection
                );
                if (this.props.onSaveBack) this.props.onSaveBack(customerPensionProtection);
                this.setState({ isLoading: false });
                this.props.callback?.();
                return;
            }

            if (!CommonHelper.isNullOrEmpty(this.props.subaccountInstructionHierarchyId)) {
                ClientRecordAction.savePensionProtectionInstruction(
                    this.props.subaccountInstructionHierarchyId,
                    PensionProtection,
                    (result) => {
                        if (result.length == 0) {
                            this.state.errorMsg.push("The information has been saved successfully.");
                            const customerPensionProtection = this.mapPensionProtectionToCustomerPensionProtection(PensionProtection);
                            if (this.props.onSaveBack) this.props.onSaveBack(customerPensionProtection);
                        } else {
                            this.setState({ errorMsg: result });
                        }
                        ClientRecordAction.getPensionProtectionInstruction(this.props.subaccountInstructionHierarchyId);
                    }
                );
            } else {
                const model = this.mapPensionProtectionToCustomerPensionProtection(PensionProtection);
                ClientRecordAction.saveCustomerPensionProtection(Number(this.props.id), model, (result) => {
                    if (result.ValidationErrors?.length == 0) {
                        this.state.errorMsg.push("The information has been saved successfully.");
                        if (this.props.onSaveBack) this.props.onSaveBack(model);
                    } else {
                        this.setState({ errorMsg: result });
                    }
                    ClientRecordAction.getCustomerPensionProtection(Number(this.props.id));
                });
            }
        }
    }

    private grantSelected(key: string): string {
        const { PensionProtection } = this.state;
        const _data = PensionProtection[key];
        let flag = null;
        GrantedTypes.forEach((ele) => {
            if (_data[ele]) {
                flag = ele;
            }
        });
        return flag;
    }

    private dataOnchanged(key: string, value: any, entity: string) {
        const { PensionProtection, _data } = this.state;
        _data[key][entity] = value;
        PensionProtection[key][entity] = _data[key][entity];
        this.setState({ PensionProtection });
    }

    private redioDisable(key: string): boolean {
        const { PensionProtection } = this.state;
        switch (key) {
            case "PensionEnhancedProtectionDetails":
                return (
                    PensionProtection.PensionFixedProtectionDetails != null ||
                    PensionProtection.PensionFixed2014ProtectionDetails != null ||
                    PensionProtection.PensionFixed2016ProtectionDetails != null
                );
            case "PensionPrimaryProtectionDetails":
                return (
                    PensionProtection.PensionFixedProtectionDetails != null ||
                    PensionProtection.PensionFixed2014ProtectionDetails != null ||
                    PensionProtection.PensionFixed2016ProtectionDetails != null ||
                    PensionProtection.PensionIndividual2014ProtectionDetails != null ||
                    PensionProtection.PensionIndividual2016ProtectionDetails != null
                );
            case "PensionFixedProtectionDetails":
                return (
                    PensionProtection.PensionFixed2014ProtectionDetails != null ||
                    PensionProtection.PensionFixed2016ProtectionDetails != null
                );
            case "PensionFixed2014ProtectionDetails":
                return (
                    PensionProtection.PensionFixedProtectionDetails != null ||
                    PensionProtection.PensionFixed2016ProtectionDetails != null
                );
            case "PensionFixed2016ProtectionDetails":
                return (
                    PensionProtection.PensionFixed2014ProtectionDetails != null ||
                    PensionProtection.PensionFixedProtectionDetails != null
                );
            case "PensionIndividual2014ProtectionDetails":
                return PensionProtection.PensionIndividual2016ProtectionDetails != null;
            case "PensionIndividual2016ProtectionDetails":
                return PensionProtection.PensionIndividual2014ProtectionDetails != null;
            case "PensionSchemeSpecificProtectionDetails":
                return !this.state.allowEditBlockTransfer;
        }
        return false;
    }

    private emptyPensionProtection(): PensionProtectionDetails {
        return {
            PensionCreditProtectionDetails: null,
            PensionEnhancedProtectionDetails: null,
            PensionPrimaryProtectionDetails: null,
            PensionFixed2014ProtectionDetails: null,
            PensionFixed2016ProtectionDetails: null,
            PensionFixedProtectionDetails: null,
            PensionIndividual2014ProtectionDetails: null,
            PensionIndividual2016ProtectionDetails: null,
            PensionRelevantOverseasProtectionDetails: null,
            PensionSchemeSpecificProtectionDetails: null,
            PensionSevereIllHealthDeclarationDetails: null,
            PensionQropsProtectionDetails: null,
            PensionTransitionalLumpSumProtectionDetails: null,
        };
    }

    private computeAllowEditBlockTransfer(model?: CustomerPensionProtectionModel): boolean {
        const wrrProtected = model?.PensionSchemeSpecificProtection?.IsWraphubBlockTransferProtected;
        return !wrrProtected;
    }

    componentDidMount() {
        const {
            hasResetPensionProtection,
            subaccountInstructionHierarchyId,
            isNewInstruction,
            id,
            pensionProtection,
        } = this.props;

        if (hasResetPensionProtection) {
            this.setState({
                isLoading: false,
                PensionProtection: this.emptyPensionProtection(),
                _data: this.emptyPensionProtection(),
                allowEditBlockTransfer: this.computeAllowEditBlockTransfer(pensionProtection)
            });

            if (isNewInstruction && CommonHelper.isNullOrEmpty(subaccountInstructionHierarchyId)) {
                this.props.onSaveBack?.(
                    this.mapPensionProtectionToCustomerPensionProtection(this.emptyPensionProtection())
                );
            }
            return;
        }

        if (isNewInstruction && CommonHelper.isNullOrEmpty(subaccountInstructionHierarchyId)) {
            const pensionData =
                pensionProtection != null
                    ? this.populatePensionProtection(pensionProtection)
                    : this.emptyPensionProtection();

            this.setState({
                isLoading: false,
                PensionProtection: pensionData,
                _data: pensionData,
                allowEditBlockTransfer: this.computeAllowEditBlockTransfer(pensionProtection)
            });

            return;
        }

        if (!CommonHelper.isNullOrEmpty(subaccountInstructionHierarchyId)) {
            ClientRecordAction.getPensionProtectionInstruction(subaccountInstructionHierarchyId);
            return;
        }

        ClientRecordAction.getCustomerPensionProtection(Number(id));
    }

    render() {
        if (this.state.isLoading) {
            return <PartLoading isLoading={true} />;
        }

        const { PensionProtection, _data, errorMsg, isTYE2024Change } = this.state;
        const { callback } = this.props;
        const radioOptions = {
            cssWrapper: "rg-150 mb-16",
            options: [true, false],
            getText: (v) => (v ? "Yes" : "No"),
        };
        return (
            <fieldset
                className={classNames({ "panel-subtle pl-34 pt-38": !this.props.hideContainerClasses })}
                disabled={this.props?.disabled}
            >
                {!this.props.hideHeading && <h3>Pension protection</h3>}

                {errorMsg.length > 0 && (
                    <Lightbox
                        show={this.state.errorMsg.length > 0}
                        title={"Success"}
                        contentAfterTitle={null}
                        onClose={() => this.setState({ errorMsg: [] })}
                        type={LightboxType.Modal}
                        unclosable={true}
                        noOverlay={false}
                        noAutofocus={true}
                        cssWrapper="is-default-lightbox is-narrow"
                    >
                        {this.state.errorMsg.map((error, index) => {
                            return <p key={`error-${index}`}>{error}</p>;
                        })}
                        <button
                            className="btn primary"
                            onClick={() => {
                                this.setState({ errorMsg: [] });
                                if (callback) {
                                    callback();
                                }
                            }}
                        >
                            OK
                        </button>
                    </Lightbox>
                )}
                {PensionProtectionMapForDrawdown.map((e) => {
                    let child;
                    if (PensionProtection[e.key] != null) {
                        switch (e.key) {
                            case "PensionEnhancedProtectionDetails":
                                child = (
                                    <>
                                        <PercentageTextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="PCLS entitlement (%)"
                                            atMost={{
                                                errorMessage: "At most 100",
                                                value: 100,
                                            }}
                                            atLeast={{
                                                errorMessage: "At least 0",
                                                value: 0,
                                            }}
                                            value={_data.PensionEnhancedProtectionDetails?.PclsEntitlement}
                                            onChange={(v) => {
                                                this.dataOnchanged(
                                                    "PensionEnhancedProtectionDetails",
                                                    v,
                                                    "PclsEntitlement"
                                                );
                                            }}
                                            onBlur={(v) => {
                                                this.dataOnchanged(
                                                    "PensionEnhancedProtectionDetails",
                                                    v == null ? null : Number(CommonHelper.downFixed(v, 2)),
                                                    "PclsEntitlement"
                                                );
                                            }}
                                        />
                                        <TextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="Certificate number"
                                            maxLength={10}
                                            value={_data.PensionEnhancedProtectionDetails?.CertificateNumber}
                                            onChange={(v) => {
                                                this.dataOnchanged(
                                                    "PensionEnhancedProtectionDetails",
                                                    v,
                                                    "CertificateNumber"
                                                );
                                            }}
                                        />
                                        {isTYE2024Change &&
                                            <>
                                                <RadiosEditor
                                                    cssWrapper="radio-group rg-150 mb-23"
                                                    label="Was Enhanced Protection applied for before 15th March 2023?"
                                                    selected={_data.PensionEnhancedProtectionDetails?.HasBeenAppliedBefore15032023 ?? false}
                                                    options={[true, false]}
                                                    getText={(v) => {
                                                        return v ? "Yes" : "No";
                                                    }}
                                                    onChange={(v) => {
                                                        this.dataOnchanged(
                                                            "PensionEnhancedProtectionDetails",
                                                            v,
                                                            "HasBeenAppliedBefore15032023"
                                                        );
                                                    }}
                                                />
                                                <NumberTextBoxEditor
                                                    cssWrapper="control-size-430"
                                                    label="Maximum tax-free entitlement from this accumulation arrangement as at 05/04/2023"
                                                    tooltip="This is the maximum tax-free lump sum entitlement that the member could have taken from this pension as at 05/04/2023"
                                                    maxNumberOfDecimalPlaces={{
                                                        errorMessage: "Expected value to 2dp.Please re-enter",
                                                        value: 2,
                                                    }}
                                                    maxLength={15}
                                                    value={_data.PensionEnhancedProtectionDetails?.MaximumTFEFromArrAt05042023}
                                                    onChange={(v) => {
                                                        this.dataOnchanged(
                                                            "PensionEnhancedProtectionDetails",
                                                            v,
                                                            "MaximumTFEFromArrAt05042023"
                                                        );
                                                    }}
                                                    onBlur={(v) => {
                                                        this.dataOnchanged(
                                                            "PensionEnhancedProtectionDetails",
                                                            v == null ? null : Number(CommonHelper.downFixed(v, 2)),
                                                            "MaximumTFEFromArrAt05042023"
                                                        );
                                                    }}
                                                    required="Please enter maximum tax-free entitlement from this accumulation arrangement as at 05/04/2023"
                                                />
                                                <NumberTextBoxEditor
                                                    cssWrapper="control-size-430"
                                                    label="Maximum tax-free entitlement from this accumulation arrangement as at 05/04/2024"
                                                    tooltip="This is the maximum tax-free lump sum entitlement that the member could have taken from this pension as at 05/04/2024"
                                                    maxNumberOfDecimalPlaces={{
                                                        errorMessage: "Expected value to 2dp.Please re-enter",
                                                        value: 2,
                                                    }}
                                                    maxLength={15}
                                                    value={_data.PensionEnhancedProtectionDetails?.MaximumTFEFromArrAt05042024}
                                                    onChange={(v) => {
                                                        this.dataOnchanged(
                                                            "PensionEnhancedProtectionDetails",
                                                            v,
                                                            "MaximumTFEFromArrAt05042024"
                                                        );
                                                    }}
                                                    onBlur={(v) => {
                                                        this.dataOnchanged(
                                                            "PensionEnhancedProtectionDetails",
                                                            v == null ? null : Number(CommonHelper.downFixed(v, 2)),
                                                            "MaximumTFEFromArrAt05042024"
                                                        );
                                                    }}
                                                    required="Please enter maximum tax-free entitlement from this accumulation arrangement as at 05/04/2024"
                                                />
                                                <NumberTextBoxEditor
                                                    cssWrapper="control-size-430"
                                                    label="Total accumulation value across all accumulation pension rights as at 05/04/2024"
                                                    tooltip="This is the total value of the member's accumulation pension rights, not just this scheme"
                                                    maxNumberOfDecimalPlaces={{
                                                        errorMessage: "Expected value to 2dp.Please re-enter",
                                                        value: 2,
                                                    }}
                                                    maxLength={15}
                                                    value={_data.PensionEnhancedProtectionDetails?.TotalAccumulationValueAt05042024}
                                                    onChange={(v) => {
                                                        this.dataOnchanged(
                                                            "PensionEnhancedProtectionDetails",
                                                            v,
                                                            "TotalAccumulationValueAt05042024"
                                                        );
                                                    }}
                                                    onBlur={(v) => {
                                                        this.dataOnchanged(
                                                            "PensionEnhancedProtectionDetails",
                                                            v == null ? null : Number(CommonHelper.downFixed(v, 2)),
                                                            "TotalAccumulationValueAt05042024"
                                                        );
                                                    }}
                                                    required={_data.PensionEnhancedProtectionDetails?.PclsEntitlement ? ""
                                                        : "Please enter total accumulation value across all accumulation pension rights as at 05/04/2024"}
                                                />
                                            </>
                                        }

                                    </>
                                );
                                break;
                            case "PensionPrimaryProtectionDetails":
                                child = (
                                    <>
                                        <NumberTextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="PCLS entitlement (£)"
                                            maxLength={15}
                                            value={_data.PensionPrimaryProtectionDetails.PclsEntitlement}
                                            onChange={(v) => {
                                                this.dataOnchanged(
                                                    "PensionPrimaryProtectionDetails",
                                                    v,
                                                    "PclsEntitlement"
                                                );
                                            }}
                                            onBlur={(v) => {
                                                this.dataOnchanged(
                                                    "PensionPrimaryProtectionDetails",
                                                    v == null ? null : Number(CommonHelper.downFixed(v, 2)),
                                                    "PclsEntitlement"
                                                );
                                            }}
                                        />
                                        <TextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="Certificate number"
                                            maxLength={10}
                                            value={_data.PensionPrimaryProtectionDetails.CertificateNumber}
                                            onChange={(v) => {
                                                this.dataOnchanged(
                                                    "PensionPrimaryProtectionDetails",
                                                    v,
                                                    "CertificateNumber"
                                                );
                                            }}
                                        />
                                        <NumberTextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="Enhancement factor number"
                                            maxLength={15}
                                            validationRules={[
                                                {
                                                    errorMessage: "Please enter Enhancement factor number",
                                                    validator: (v) => {
                                                        return !!_data.PensionPrimaryProtectionDetails
                                                            .EnhancementFactor;
                                                    },
                                                },
                                            ]}
                                            value={_data.PensionPrimaryProtectionDetails.EnhancementFactor}
                                            onChange={(v) => {
                                                this.dataOnchanged(
                                                    "PensionPrimaryProtectionDetails",
                                                    v,
                                                    "EnhancementFactor"
                                                );
                                            }}
                                            onBlur={(v) => {
                                                this.dataOnchanged(
                                                    "PensionPrimaryProtectionDetails",
                                                    v == null ? null : Number(CommonHelper.downFixed(v, 2)),
                                                    "EnhancementFactor"
                                                );
                                            }}
                                        />
                                    </>
                                );
                                break;
                            case "PensionFixedProtectionDetails":
                                child = (
                                    <>
                                        <TextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="Certificate number (Fixed protection 2012)"
                                            maxLength={10}
                                            value={_data.PensionFixedProtectionDetails.CertificateNumber}
                                            onChange={(v) => {
                                                this.dataOnchanged(
                                                    "PensionFixedProtectionDetails",
                                                    v,
                                                    "CertificateNumber"
                                                );
                                            }}
                                        />
                                        {isTYE2024Change &&
                                            <RadiosEditor
                                                cssWrapper="radio-group rg-150 mb-23"
                                                label="Was Fixed Protection 2012 applied for before 15th March 2023?"
                                                selected={_data.PensionFixedProtectionDetails?.HasBeenAppliedBefore15032023 ?? false}
                                                options={[true, false]}
                                                getText={(v) => {
                                                    return v ? "Yes" : "No";
                                                }}
                                                onChange={(v) => {
                                                    this.dataOnchanged(
                                                        "PensionFixedProtectionDetails",
                                                        v,
                                                        "HasBeenAppliedBefore15032023"
                                                    );
                                                }}
                                                required="Please enter certificate number"
                                            />
                                        }
                                    </>
                                );
                                break;
                            case "PensionFixed2014ProtectionDetails":
                                child = (
                                    <>
                                        <TextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="Certificate number (Fixed protection 2014)"
                                            maxLength={10}
                                            value={_data.PensionFixed2014ProtectionDetails.CertificateNumber}
                                            onChange={(v) => {
                                                this.dataOnchanged(
                                                    "PensionFixed2014ProtectionDetails",
                                                    v,
                                                    "CertificateNumber"
                                                );
                                            }}
                                        />
                                        {isTYE2024Change &&
                                            <RadiosEditor
                                                cssWrapper="radio-group rg-150 mb-23"
                                                label="Was Fixed Protection 2014 applied for before 15th March 2023?"
                                                selected={_data.PensionFixed2014ProtectionDetails?.HasBeenAppliedBefore15032023}
                                                options={[true, false]}
                                                getText={(v) => {
                                                    return v ? "Yes" : "No";
                                                }}
                                                onChange={(v) => {
                                                    this.dataOnchanged(
                                                        "PensionFixed2014ProtectionDetails",
                                                        v,
                                                        "HasBeenAppliedBefore15032023"
                                                    );
                                                }}
                                            />
                                        }
                                    </>
                                );
                                break;
                            case "PensionFixed2016ProtectionDetails":
                                child = (
                                    <>
                                        <TextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="Certificate number (Fixed protection 2016)"
                                            maxLength={10}
                                            value={_data.PensionFixed2016ProtectionDetails.CertificateNumber}
                                            onChange={(v) => {
                                                this.dataOnchanged(
                                                    "PensionFixed2016ProtectionDetails",
                                                    v,
                                                    "CertificateNumber"
                                                );
                                            }}
                                            required="Please enter certificate number"
                                        />
                                        {isTYE2024Change &&
                                            <RadiosEditor
                                                cssWrapper="radio-group rg-150 mb-23"
                                                label="Was Fixed Protection 2016 applied for before 15th March 2023?"
                                                selected={_data.PensionFixed2016ProtectionDetails?.HasBeenAppliedBefore15032023}
                                                options={[true, false]}
                                                getText={(v) => {
                                                    return v ? "Yes" : "No";
                                                }}
                                                onChange={(v) => {
                                                    this.dataOnchanged(
                                                        "PensionFixed2016ProtectionDetails",
                                                        v,
                                                        "HasBeenAppliedBefore15032023"
                                                    );
                                                }}
                                            />
                                        }
                                    </>
                                );
                                break;
                            case "PensionIndividual2014ProtectionDetails":
                                child = (
                                    <>
                                        <TextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="Certificate number (Individual protection 2014)"
                                            maxLength={10}
                                            value={_data.PensionIndividual2014ProtectionDetails.CertificateNumber}
                                            onChange={(v) => {
                                                this.dataOnchanged(
                                                    "PensionIndividual2014ProtectionDetails",
                                                    v,
                                                    "CertificateNumber"
                                                );
                                            }}
                                        />
                                        <NumberTextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="Individual Protection 2014 LTA amount"
                                            required="Please enter Individual Protection 2014 LTA amount"
                                            atMost={{
                                                errorMessage: "A value between 1250000 and 1500000 is expected.",
                                                value: 1500000,
                                            }}
                                            atLeast={{
                                                errorMessage: "A value between 1250000 and 1500000 is expected.",
                                                value: 1250000,
                                            }}
                                            value={_data.PensionIndividual2014ProtectionDetails.Amount}
                                            onChange={(v) => {
                                                this.dataOnchanged(
                                                    "PensionIndividual2014ProtectionDetails",
                                                    v,
                                                    "Amount"
                                                );
                                            }}
                                            onBlur={(v) => {
                                                this.dataOnchanged(
                                                    "PensionIndividual2014ProtectionDetails",
                                                    v == null ? null : Number(CommonHelper.downFixed(v, 2)),
                                                    "Amount"
                                                );
                                            }}
                                        />
                                    </>
                                );
                                break;
                            case "PensionIndividual2016ProtectionDetails":
                                child = (
                                    <>
                                        <TextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="Certificate number (Individual protection 2016)"
                                            maxLength={10}
                                            value={_data.PensionIndividual2016ProtectionDetails.CertificateNumber}
                                            onChange={(v) => {
                                                this.dataOnchanged(
                                                    "PensionIndividual2016ProtectionDetails",
                                                    v,
                                                    "CertificateNumber"
                                                );
                                            }}
                                        />
                                        <NumberTextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="Individual Protection 2016 LTA amount"
                                            required="Please enter Individual Protection 2016 LTA amount"
                                            atMost={{
                                                errorMessage: "A value between 1000000 and 1250000 is expected.",
                                                value: 1250000,
                                            }}
                                            atLeast={{
                                                errorMessage: "A value between 1000000 and 1250000 is expected.",
                                                value: 1000000,
                                            }}
                                            value={_data.PensionIndividual2016ProtectionDetails.Amount}
                                            onChange={(v) => {
                                                this.dataOnchanged(
                                                    "PensionIndividual2016ProtectionDetails",
                                                    v,
                                                    "Amount"
                                                );
                                            }}
                                            onBlur={(v) => {
                                                this.dataOnchanged(
                                                    "PensionIndividual2016ProtectionDetails",
                                                    v == null ? null : Number(CommonHelper.downFixed(v, 2)),
                                                    "Amount"
                                                );
                                            }}
                                        />
                                    </>
                                );
                                break;
                            case "PensionRelevantOverseasProtectionDetails":
                            case "PensionQropsProtectionDetails":
                            case "PensionCreditProtectionDetails":
                                child = (
                                    <>
                                        <TextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="Certificate number"
                                            maxLength={10}
                                            value={_data[e.key].CertificateNumber}
                                            onChange={(v) => {
                                                PensionProtection[e.key].CertificateNumber = v;
                                                this.setState({ PensionProtection });
                                            }}
                                        />
                                        <NumberTextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="Enhancement factor number"
                                            validationRules={[
                                                {
                                                    errorMessage: "Please enter Enhancement factor number",
                                                    validator: (v) => {
                                                        return !!_data[e.key].EnhancementFactor;
                                                    },
                                                },
                                            ]}
                                            maxLength={15}
                                            value={PensionProtection[e.key].EnhancementFactor}
                                            onChange={(v) => {
                                                this.dataOnchanged(e.key, v, "EnhancementFactor");
                                            }}
                                            onBlur={(v) => {
                                                this.dataOnchanged(
                                                    e.key,
                                                    v == null ? null : Number(CommonHelper.downFixed(v, 2)),
                                                    "EnhancementFactor"
                                                );
                                            }}
                                        />
                                        <RadiosEditor
                                            cssWrapper="control-size-430 mb-16"
                                            label="Granted"
                                            selected={this.grantSelected(e.key)}
                                            options={GrantedTypes}
                                            getText={(v) => GrantedTypesMap[v]}
                                            onChange={(v) => {
                                                GrantedTypes.forEach((t) => {
                                                    t == v
                                                        ? (PensionProtection[e.key][t] = true)
                                                        : (PensionProtection[e.key][t] = false);
                                                });
                                                this.setState({ PensionProtection });
                                            }}
                                            required="Please select Granted"
                                        />
                                    </>
                                );
                                break;
                            case "PensionTransitionalLumpSumProtectionDetails":
                                child = (
                                    <>
                                        <NumberTextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="Transitional Lump Sum Allowance Used (£)"
                                            maxNumberOfDecimalPlaces={{
                                                errorMessage: "Expected value to 2dp.Please re-enter",
                                                value: 2,
                                            }}
                                            validationRules={[
                                                {
                                                    errorMessage: "Please enter Transitional Lump Sum Allowance Used value",
                                                    validator: (v) => {
                                                        return (
                                                            _data[e.key].TransitionalLumpSumAllowance
                                                        );
                                                    },
                                                },
                                            ]}
                                            maxLength={15}
                                            value={_data[e.key].TransitionalLumpSumAllowance}
                                            onChange={(v) => {
                                                this.dataOnchanged(e.key, v, "TransitionalLumpSumAllowance");
                                            }}
                                            onBlur={(v) => {
                                                this.dataOnchanged(
                                                    e.key,
                                                    v == null ? null : Number(CommonHelper.downFixed(v, 2)),
                                                    "TransitionalLumpSumAllowance"
                                                );
                                            }}
                                        />
                                        <NumberTextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="Transitional Lump Sum and Death Benefit Allowance Used (£)"
                                            maxNumberOfDecimalPlaces={{
                                                errorMessage: "Expected value to 2dp.Please re-enter",
                                                value: 2,
                                            }}
                                            validationRules={[
                                                {
                                                    errorMessage: "Please enter Transitional Lump Sum and Death Benefit Allowance Used value",
                                                    validator: (v) => {
                                                        return (
                                                            _data[e.key].TransitionalLumpSumDeathBenefitAllowance
                                                        );
                                                    },
                                                },
                                            ]}
                                            maxLength={15}
                                            value={_data[e.key].TransitionalLumpSumDeathBenefitAllowance}
                                            onChange={(v) => {
                                                this.dataOnchanged(e.key, v, "TransitionalLumpSumDeathBenefitAllowance");
                                            }}
                                            onBlur={(v) => {
                                                this.dataOnchanged(
                                                    e.key,
                                                    v == null ? null : Number(CommonHelper.downFixed(v, 2)),
                                                    "TransitionalLumpSumDeathBenefitAllowance"
                                                );
                                            }}
                                        />
                                    </>
                                );
                                break;
                            case "PensionSchemeSpecificProtectionDetails":
                                child = (
                                    <>
                                        <NumberTextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="Protected PCLS value"
                                            validationRules={[
                                                {
                                                    errorMessage: "Please enter Protected PCLS value",
                                                    validator: (v) => {
                                                        return (
                                                            !_data[e.key].ADayFundValue || _data[e.key].PclsEntitlement
                                                        );
                                                    },
                                                },
                                            ]}
                                            maxLength={15}
                                            value={_data[e.key].PclsEntitlement}
                                            onChange={(v) => {
                                                this.dataOnchanged(e.key, v, "PclsEntitlement");
                                            }}
                                            onBlur={(v) => {
                                                this.dataOnchanged(
                                                    e.key,
                                                    v == null ? null : Number(CommonHelper.downFixed(v, 2)),
                                                    "PclsEntitlement"
                                                );
                                            }}
                                            disabled={!this.state.allowEditBlockTransfer}
                                        />
                                        <NumberTextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="ADay fund value"
                                            validationRules={[
                                                {
                                                    errorMessage: "Please enter ADay fund value",
                                                    validator: (v) => {
                                                        return (
                                                            !_data[e.key].PclsEntitlement || _data[e.key].ADayFundValue
                                                        );
                                                    },
                                                },
                                            ]}
                                            maxLength={15}
                                            value={_data[e.key].ADayFundValue}
                                            onChange={(v) => {
                                                this.dataOnchanged(e.key, v, "ADayFundValue");
                                            }}
                                            onBlur={(v) => {
                                                this.dataOnchanged(
                                                    e.key,
                                                    v == null ? null : Number(CommonHelper.downFixed(v, 2)),
                                                    "ADayFundValue"
                                                );
                                            }}
                                            disabled={!this.state.allowEditBlockTransfer}
                                        />
                                        <NumberTextBoxEditor
                                            cssWrapper="control-size-430"
                                            label="Protected retirement age"
                                            required={
                                                !_data[e.key].PclsEntitlement && !_data[e.key].ADayFundValue
                                                    ? "Please enter Protected retirement age"
                                                    : null
                                            }
                                            atMost={{
                                                errorMessage: "A value between 35 and 54 is expected.",
                                                value: 54,
                                            }}
                                            atLeast={{
                                                errorMessage: "A value between 35 and 54 is expected.",
                                                value: 35,
                                            }}
                                            value={_data[e.key].ProtectedRetirementAge}
                                            onChange={(v) => {
                                                this.dataOnchanged(e.key, v, "ProtectedRetirementAge");
                                            }}
                                            disabled={!this.state.allowEditBlockTransfer}
                                        />
                                    </>
                                );
                            default:
                                break;
                        }
                    }
                    return (
                        (!this.props.displaySubmittedOptionsOnly || PensionProtection[e.key]) &&
                        <>
                            <RadiosEditor
                                label={e.value}
                                selected={PensionProtection[e.key] != null}
                                onChange={(v) => {
                                    if (v) {
                                        if (_data[e.key] == null) {
                                            _data[e.key] = {};
                                        }
                                        PensionProtection[e.key] = _data[e.key];
                                        if (e.key == "PensionFixedProtectionDetails") {
                                            this.dataOnchanged(
                                                "PensionFixedProtectionDetails",
                                                v == true ? 0 : v,
                                                "CertificateNumber"
                                            );
                                            PensionProtection.PensionFixed2014ProtectionDetails = null;
                                            PensionProtection.PensionFixed2016ProtectionDetails = null;
                                            PensionProtection.PensionEnhancedProtectionDetails = null;
                                            PensionProtection.PensionPrimaryProtectionDetails = null;
                                        }
                                        if (e.key == "PensionFixed2014ProtectionDetails") {
                                            PensionProtection.PensionFixedProtectionDetails = null;
                                            PensionProtection.PensionFixed2016ProtectionDetails = null;
                                            PensionProtection.PensionEnhancedProtectionDetails = null;
                                            PensionProtection.PensionPrimaryProtectionDetails = null;
                                        }
                                        if (e.key == "PensionFixed2016ProtectionDetails") {
                                            PensionProtection.PensionFixed2014ProtectionDetails = null;
                                            PensionProtection.PensionFixedProtectionDetails = null;
                                            PensionProtection.PensionEnhancedProtectionDetails = null;
                                            PensionProtection.PensionPrimaryProtectionDetails = null;
                                        }
                                        if (e.key == "PensionIndividual2014ProtectionDetails") {
                                            PensionProtection.PensionIndividual2016ProtectionDetails = null;
                                            PensionProtection.PensionPrimaryProtectionDetails = null;
                                        }
                                        if (e.key == "PensionIndividual2016ProtectionDetails") {
                                            PensionProtection.PensionIndividual2014ProtectionDetails = null;
                                            PensionProtection.PensionPrimaryProtectionDetails = null;
                                        }
                                    } else {
                                        PensionProtection[e.key] = null;
                                    }
                                    this.setState({ PensionProtection });
                                }}
                                disabled={this.redioDisable(e.key)}
                                required={"Please select " + e.value}
                                {...radioOptions}
                            />
                            {child}
                        </>
                    );
                })}
                {!this.props.displaySubmittedOptionsOnly &&
                    <div className="col-12 col-sm-4 pl-0 pb-20">
                        <button className="btn primary label-offset" onClick={() => this.onSave()}>
                            Save
                        </button>
                    </div>
                }
            </fieldset>
        );
    }
}
